import { WindowController, WindowCoords, WindowBind, WindowEdges } from './window.controller';

export enum ResizeDirection {
  None = 0,
  Top = 1,
  Bottom = 2,
  Left = 4,
  Right = 8,
};

export class WindowContextClass {
  readonly queue: Array<WindowController>;
  private minimalGap: number = 20;
  private centeringGap: number = 2;
  private hidingFixGap: number = 46;
  private _container: HTMLDivElement | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private minimalLayer: number = 1;

  constructor({ minLayer = 1 }: { minLayer?: number }) {
    this.queue = [];
    this.minimalLayer = minLayer;
  }

  get container() {
    return this._container;
  }

  set container(el:HTMLDivElement | null) {
    this._container = el;
    
    if (!this._container) {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      return
    }

    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(() => {
        for (const controller of this.queue) {
          this.adjustPositionAndSize(controller);
        }
      });
    }

    this._resizeObserver.observe(this._container!);
  }

  private adjustPositionAndSize(controller: WindowController, moveDelta?: WindowCoords) {
    if (!this.container) return;
    if (!controller.element) return;
    if (controller.element.style.display === 'none') return;
    const containerRect = this.container.getBoundingClientRect();
    const windowRect = controller.element.getBoundingClientRect();
    let { left, right, top, bottom, width, height } = windowRect;

    if (moveDelta) {
      left += moveDelta.x;
      right += moveDelta.x;
      top += moveDelta.y;
      bottom += moveDelta.y;
    }

    if (width > containerRect.width) {
      left = containerRect.left;
      right = containerRect.right;
    } else {
      const leftAdjust = Math.max(0, containerRect.left - left);
      const rightAdjust = Math.min(0, containerRect.right - right);
      left += (leftAdjust + rightAdjust);
      right += (leftAdjust + rightAdjust);
    }

    if (height > containerRect.height) {
      top = containerRect.top;
      bottom = containerRect.bottom;
    } else {
      const topAdjust = Math.max(0, containerRect.top - top);
      const bottomAdjust = Math.min(0, containerRect.bottom - bottom);
      top += (topAdjust + bottomAdjust);
      bottom += (topAdjust + bottomAdjust);
    }

    const containerCenter = (containerRect.left + containerRect.right) / 2;
    const windowCenter = (left + right) / 2;
    const hDelta = containerCenter - windowCenter;

    const containerMiddle = (containerRect.top + containerRect.bottom) / 2;
    const windowMiddle = (top + bottom) / 2;
    const vDelta = containerMiddle - windowMiddle;

    if (moveDelta) {
      if (moveDelta.x) {
        if (!(controller.bind & WindowBind.CENTER)) {
          if ((hDelta * moveDelta.x) > 0 && Math.abs(hDelta) <= this.minimalGap) controller.bind |= WindowBind.CENTER;
        } else if (Math.abs(moveDelta.x) > this.centeringGap) {
          controller.bind &= ~WindowBind.CENTER;
        }
      
        if ((moveDelta.x < 0) && Math.abs(left - containerRect.left) <= this.minimalGap) {
          controller.bind |= WindowBind.LEFT;
        } else {
          controller.bind &= ~WindowBind.LEFT;
        }
      
        if ((moveDelta.x > 0) && Math.abs(right - containerRect.right) <= this.minimalGap) {
          controller.bind |= WindowBind.RIGHT;
        } else {
          controller.bind &= ~WindowBind.RIGHT;
        }
      }

      if (moveDelta.y) {
        if (!(controller.bind & WindowBind.MIDDLE)) {
          if ((vDelta * moveDelta.y) > 0 && Math.abs(vDelta) <= this.minimalGap) controller.bind |= WindowBind.MIDDLE;
        } else if (Math.abs(moveDelta.y) > this.centeringGap) {
          controller.bind &= ~WindowBind.MIDDLE;
        }
        
        if ((moveDelta.y < 0) && Math.abs(top - containerRect.top) <= this.minimalGap) {
          controller.bind |= WindowBind.TOP;
        } else {
          controller.bind &= ~WindowBind.TOP;
        }
        
        if ((moveDelta.y > 0) && Math.abs(bottom - containerRect.bottom) <= this.minimalGap) {
          controller.bind |= WindowBind.BOTTOM;
        } else {
          controller.bind &= ~WindowBind.BOTTOM;
        }
      }
    }

    if (controller.bind) {
      if (controller.bind & WindowBind.CENTER) {
        left += hDelta;
        right += hDelta;
      } else if (controller.bind & WindowBind.LEFT) {
        const w = right - left;
        left = containerRect.left;
        right = left + w;
      } else if (controller.bind & WindowBind.RIGHT) {
        const w = right - left;
        right = containerRect.right;
        left = right - w;
      }

      if (controller.bind & WindowBind.MIDDLE) {
        top += vDelta;
        bottom += vDelta;
      } else if (controller.bind & WindowBind.TOP) {
        const h = bottom - top;
        top = containerRect.top;
        bottom = top + h;
      } else if (controller.bind & WindowBind.BOTTOM) {
        const h = bottom - top;
        bottom = containerRect.bottom;
        top = bottom - h;
      }
    }

    controller.resize({
      x: (right - left) - windowRect.width,
      y: (bottom - top) - windowRect.height,
    });

    controller.setPosition({
      left: left - containerRect.left,
      right: containerRect.right - right,
      top: top - containerRect.top,
      bottom: containerRect.bottom - bottom,
    });
  }

  add(wc: WindowController): void {
    let i = this.queue.findIndex((controller) => Object.is(controller, wc));
    if (i < 0) {
      const current = this.queue.length ? this.queue[this.queue.length - 1] : null;
      this.queue.push(wc);
      wc.setLayer(this.minimalLayer + this.queue.length - 1);
      if (current) {
        this.adjustPositionAndSize(wc);
        if (wc.hides(current)) {
          let x = 0;
          let y = 0;

          const wcRect = wc.getBoundingClientRect();

          if (this.container && wcRect) {
            const { left, right, top, bottom } = this.container.getBoundingClientRect();
            let m1 = wcRect.left - left;
            let m2 = right - wcRect.right;
            x = (m1 >= m2) ? -Math.min(m1, this.hidingFixGap) : Math.min(m2, this.hidingFixGap);

            m1 = wcRect.top - top;
            m2 = bottom - wcRect.bottom;

            y = (m1 >= m2) ? -Math.min(m1, this.hidingFixGap) : Math.min(m2, this.hidingFixGap);
          }

          this.move(wc, { x, y });
        }
        return;
      }
    } else {
      this.raise(wc);
    }
    this.adjustPositionAndSize(wc);
  }

  raise(wc: WindowController): void {
    const target = this.queue.length - 1;
    let i = this.queue.findIndex((controller) => Object.is(controller, wc));
    if (i < 0 || i === target) {
      return;
    }
    const temp = this.queue[i];
    for (i++; i < this.queue.length; i++) {
      this.queue[i - 1] = this.queue[i];
      this.queue[i - 1].setLayer(this.minimalLayer + i - 1);
    }
    this.queue[target] = temp;
    temp.setLayer(this.minimalLayer + target);
  }

  reduce(wc: WindowController): void {
    let i = this.queue.findIndex((controller) => Object.is(controller, wc));
    if (i < 0) {
      return;
    }
    const temp = this.queue[i];
    for (; i > 0; i--) {
      this.queue[i] = this.queue[i - 1];
      this.queue[i].setLayer(this.minimalLayer + i);
    }
    this.queue[0] = temp;
    temp.setLayer(this.minimalLayer + 0);
  }

  remove(wc: WindowController): void {
    let i = this.queue.findIndex((controller) => Object.is(controller, wc));
    if (i >= 0) {
      this.queue.splice(i, 1);
      for (; i < this.queue.length; i++) {
        this.queue[i].setLayer(i);
      }
    }
  }

  isInside(point: WindowCoords): boolean {
    if (!this.container) {
      return false;
    }
    const containerRect = this.container.getBoundingClientRect();
    if (
      point.x < containerRect.left
      || point.x > containerRect.right
      || point.y < containerRect.top
      || point.y > containerRect.bottom
    ) {
      return false;
    }
    return true;
  }

  move(wc: WindowController, delta: WindowCoords) {
    if (!this.container) {
      return;
    }
    this.adjustPositionAndSize(wc, delta);
  }

  resize(wc: WindowController, { x, y }: WindowCoords, direction: ResizeDirection): void {
    if (!this.container) {
      return;
    }

    const delta = { x: 0, y: 0 };

    if ((direction & ResizeDirection.Left) === ResizeDirection.Left) {
      delta.x = -x;
    }
    if ((direction & ResizeDirection.Top) === ResizeDirection.Top) {
      delta.y = -y;
    }
    if ((direction & ResizeDirection.Right) === ResizeDirection.Right) {
      delta.x = x;
    }
    if ((direction & ResizeDirection.Bottom) === ResizeDirection.Bottom) {
      delta.y = y;
    }

    wc.resize(delta);

    if ((direction & ResizeDirection.Left) || (direction & ResizeDirection.Top)) {
      this.move(
        wc,
        {
          x: (direction & ResizeDirection.Left) ? x : 0,
          y: (direction & ResizeDirection.Top) ? y: 0,
        },
      );
    }    
  }
}